import { useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";

// Pull-to-refresh táctil global (cubre toda la app vía GlobalPullToRefresh).
// Diseñado para funcionar en navegador, PWA y Android WebView (APK / Play).
//
// Por qué la versión anterior fallaba en Android WebView:
//  • Los listeners se atan al div contenedor; si un hijo detiene la
//    propagación o el WebView despacha el toque a un contenedor interno
//    scrolleable, el gesto nunca llega al handler.
//  • `preventDefault` en un listener bubbling (no-capture) llega tarde y el
//    overscroll nativo del WebView ya consumió el movimiento.
//
// Esta versión ata touchstart/touchmove/touchend a `window` con `capture:true`
// (recibe el evento ANTES que cualquier hijo), usa `passive:false` en touchmove
// para poder `preventDefault()` y parar el overscroll del sistema, y detecta
// el contenedor scrolleable más cercano al punto de toque para saber si el
// usuario está "en el tope" (no sólo `window.scrollY`), evitando disparos
// falsos dentro de listas internas.
//
// `disabled` / `disabledFn` bloquean el gesto cuando hay un modal/sheet abierto.
// No mueve el contenido: sólo muestra un spinner bajo el header fijo.
// A11y (TalkBack): el estado se anuncia vía region aria-live.
const THRESHOLD = 70;
const MAX = 110;

function nearestScrollTop(target) {
  // true si el elemento tocado (y sus ancestros) están en su tope de scroll.
  let node = target;
  while (node && node !== document.body) {
    const style = node.nodeType === 1 ? window.getComputedStyle(node) : null;
    const overflowY = style?.overflowY;
    if (overflowY === "auto" || overflowY === "scroll") {
      if (node.scrollTop > 0) return false;
    }
    node = node.parentElement;
  }
  // llegó al body: comprobar scroll del documento.
  const docEl = document.scrollingElement || document.documentElement;
  return (docEl?.scrollTop || window.scrollY || 0) <= 0;
}

export default function PullToRefresh({ onRefresh, disabled, disabledFn, children }) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [announce, setAnnounce] = useState("");
  const startY = useRef(0);
  const pulling = useRef(false);
  const pullRef = useRef(0);
  const onRefreshRef = useRef(onRefresh);
  const containerRef = useRef(null);

  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

  useEffect(() => {
    if (!refreshing) return;
    let active = true;
    setAnnounce("Actualizando contenido…");
    (async () => {
      try {
        await onRefreshRef.current?.();
      } finally {
        if (!active) return;
        setRefreshing(false);
        setPull(0);
        pullRef.current = 0;
        setAnnounce("Contenido actualizado");
        window.setTimeout(() => active && setAnnounce(""), 1200);
      }
    })();
    return () => { active = false; };
  }, [refreshing]);

  useEffect(() => {
    if (disabled) {
      setPull(0);
      pullRef.current = 0;
      pulling.current = false;
    }
  }, [disabled]);

  useEffect(() => {
    const isBlocked = () => disabled || refreshing || (disabledFn && disabledFn());

    const onTouchStart = (e) => {
      if (isBlocked()) return;
      if (e.touches.length !== 1) return;
      // ¿El punto de toque está en el tope de su contenedor scrolleable?
      if (!nearestScrollTop(e.target)) { pulling.current = false; return; }
      startY.current = e.touches[0].clientY;
      pulling.current = true;
    };

    const onTouchMove = (e) => {
      if (!pulling.current) return;
      if (e.touches.length !== 1) return;
      if (disabledFn && disabledFn()) { pulling.current = false; setPull(0); pullRef.current = 0; return; }
      const dy = e.touches[0].clientY - startY.current;
      // Sólo tira hacia abajo Y el contenedor sigue en el tope.
      const atTop = nearestScrollTop(e.target);
      if (dy > 0 && atTop) {
        // Frena el overscroll/pull-to-refresh nativo del navegador/WebView.
        if (e.cancelable) e.preventDefault();
        const next = Math.min(dy * 0.5, MAX);
        pullRef.current = next;
        setPull(next);
      } else if (dy <= 0) {
        pulling.current = false;
        pullRef.current = 0;
        setPull(0);
      } else if (!atTop) {
        // el usuario scrolló un contenedor interno: soltar el gesto.
        pulling.current = false;
        pullRef.current = 0;
        setPull(0);
      }
    };

    const onTouchEnd = () => {
      if (!pulling.current) return;
      pulling.current = false;
      if (pullRef.current >= THRESHOLD) {
        setRefreshing(true);
        setPull(THRESHOLD);
        pullRef.current = THRESHOLD;
      } else {
        setPull(0);
        pullRef.current = 0;
      }
    };

    const onTouchCancel = () => {
      pulling.current = false;
      pullRef.current = 0;
      setPull(0);
    };

    // capture:true → recibimos el toque antes que cualquier hijo; pasive:false
    // en touchmove para poder preventDefault() y parar el overscroll nativo.
    window.addEventListener("touchstart", onTouchStart, { passive: true, capture: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false, capture: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true, capture: true });
    window.addEventListener("touchcancel", onTouchCancel, { passive: true, capture: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart, { capture: true });
      window.removeEventListener("touchmove", onTouchMove, { capture: true });
      window.removeEventListener("touchend", onTouchEnd, { capture: true });
      window.removeEventListener("touchcancel", onTouchCancel, { capture: true });
    };
  }, [disabled, refreshing, disabledFn]);

  const indicatorTop = pull > 0 ? pull : refreshing ? THRESHOLD : 0;

  return (
    <div ref={containerRef} className="relative" style={{ overscrollBehavior: "contain" }}>
      <div
        className="absolute left-0 right-0 flex items-center justify-center pointer-events-none"
        style={{
          top: indicatorTop,
          transform: "translateY(-100%)",
          transition: pulling.current ? "none" : "top 0.2s ease",
        }}
        aria-hidden="true"
      >
        {refreshing ? (
          <Loader2 className="w-5 h-5 text-cyan animate-spin" />
        ) : (
          <RefreshCw
            className="w-5 h-5 text-cyan"
            style={{ opacity: Math.min(pull / THRESHOLD, 1) }}
          />
        )}
      </div>
      <span role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announce}
      </span>
      {children}
    </div>
  );
}